import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  withdrawalRequestService,
  AdminWithdrawalRequest,
  PotentialWithdrawal,
} from '../services';
import { useToast } from '../hook/use-toast';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  DoneAll as DoneAllIcon,
  WarningAmber as WarningAmberIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';

type PotentialSettlementTab = 'pagarme' | 'legacy';

const PIX_KEY_TYPE_LABEL: Record<string, string> = {
  cpf: 'CPF',
  cnpj: 'CNPJ',
  email: 'E-mail',
  phone: 'Celular',
  random: 'Chave aleatória',
};

function pixKeyLabel(type?: string) {
  if (!type) return 'Pix';
  return PIX_KEY_TYPE_LABEL[type] || 'Pix';
}

function resolvePotentialSettlementView(
  p: PotentialWithdrawal
): 'pagarme' | 'legacy' | 'mixed' {
  if (p.settlementView) return p.settlementView;
  const pagarmeNet = p.pagarmeParishNet ?? 0;
  const legacyNet =
    (p.mercadopagoCardParishNet ?? 0) +
    (p.mercadopagoPixParishNet ?? 0) +
    (p.mercadopagoAmbiguousParishNet ?? 0);
  if (pagarmeNet > 0.05 && legacyNet > 0.05) return 'mixed';
  if (pagarmeNet > 0.05 || p.pagarmeCheckoutReady || p.pagarmePixCheckoutEnabled === true) return 'pagarme';
  return 'legacy';
}

function matchesPotentialSettlementTab(
  p: PotentialWithdrawal,
  tab: PotentialSettlementTab
): boolean {
  const view = resolvePotentialSettlementView(p);
  if (tab === 'pagarme') return view === 'pagarme' || view === 'mixed';
  return view === 'legacy';
}

function formatWithdrawalCurrency(v: number) {
  return `R$ ${v.toFixed(2).replace('.', ',')}`;
}

function PotentialWithdrawalDetailRow({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 2,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {hint ? (
          <Typography variant="caption" color="text.disabled" display="block">
            {hint}
          </Typography>
        ) : null}
      </Box>
      <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', flexShrink: 0 }}>
        {value}
      </Typography>
    </Box>
  );
}

function PotentialWithdrawalCard({
  item,
  view,
  expanded,
  onToggle,
  onMarkPaid,
  markDisabled,
}: {
  item: PotentialWithdrawal;
  view: PotentialSettlementTab;
  expanded: boolean;
  onToggle: () => void;
  onMarkPaid: () => void;
  markDisabled: boolean;
}) {
  const settlementView = resolvePotentialSettlementView(item);
  const hasReconciliationWarning =
    item.reconciliationOk === false &&
    Array.isArray(item.reconciliationMessages) &&
    item.reconciliationMessages.length > 0;

  const pagarmeRecipientLabel =
    item.pagarmeRecipientBalanceReais != null
      ? formatWithdrawalCurrency(item.pagarmeRecipientBalanceReais)
      : item.pagarmeRecipientBalanceCode === 'no_recipient'
        ? 'Sem recebedor'
        : item.pagarmeRecipientBalanceCode === 'fetch_failed'
          ? 'Erro ao consultar'
          : '—';

  const statusChips = (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
      {settlementView === 'mixed' ? (
        <Chip label="Misto" size="small" color="warning" sx={{ height: 22, fontWeight: 700 }} />
      ) : null}
      {view === 'pagarme' && item.pagarmeCheckoutReady ? (
        <Chip label="Split ativo" size="small" color="success" sx={{ height: 22, fontWeight: 700 }} />
      ) : null}
      {view === 'pagarme' && !item.pagarmeCheckoutReady && item.pagarmePixCheckoutEnabled === true ? (
        <Chip label="Evento Pagarme" size="small" color="info" sx={{ height: 22, fontWeight: 700 }} />
      ) : null}
      {view === 'legacy' ? (
        <Chip label="Legado MP" size="small" variant="outlined" sx={{ height: 22, fontWeight: 700 }} />
      ) : null}
      {hasReconciliationWarning ? (
        <Chip
          icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
          label={expanded ? 'Revisar reconciliação' : 'Conferir'}
          size="small"
          color="warning"
          variant={expanded ? 'filled' : 'outlined'}
          sx={{ height: 22, fontWeight: 700 }}
        />
      ) : null}
      <Chip
        label="Sem solicitação"
        size="small"
        variant="outlined"
        sx={{ height: 22, fontSize: 11 }}
      />
    </Stack>
  );

  const summaryHint =
    view === 'pagarme'
      ? settlementView === 'mixed'
        ? `Pagarme ${formatWithdrawalCurrency(item.pagarmeParishNet ?? 0)} + MP no evento`
        : `Conta recebedora: ${pagarmeRecipientLabel}`
      : `MP estimado: ${formatWithdrawalCurrency(item.mercadopagoCardAvailableReais ?? 0)}`;

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1.5,
        overflow: 'hidden',
        borderColor: expanded ? 'primary.light' : 'divider',
        transition: 'border-color 0.2s',
      }}
    >
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          display: 'flex',
          gap: 2,
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box
          role="button"
          tabIndex={0}
          onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle();
            }
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            cursor: 'pointer',
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', borderRadius: 1 },
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
            {item.eventTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {item.parishName}
            {(item.parishCity || item.parishState) &&
              ` • ${[item.parishCity, item.parishState].filter(Boolean).join(' / ')}`}
          </Typography>
          {statusChips}
          {!expanded ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {summaryHint} • Clique para ver detalhes
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            flexShrink: 0,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <Box sx={{ minWidth: 108 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Disponível
            </Typography>
            <Typography variant="h6" fontWeight={800} color="primary.main" lineHeight={1.2}>
              {formatWithdrawalCurrency(item.availableBalance)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.totalRegistrations} inscrição(ões)
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton
              size="small"
              onClick={onToggle}
              aria-expanded={expanded}
              aria-label={expanded ? 'Recolher detalhes' : 'Ver detalhes do saque'}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                transform: expanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DoneAllIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onMarkPaid();
              }}
              disabled={markDisabled}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Marcar como pago
            </Button>
          </Stack>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            pb: 2,
            pt: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', pt: 1.5, mb: 0.5 }}>
            Resumo financeiro
          </Typography>
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, px: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <PotentialWithdrawalDetailRow
              label="Saldo disponível (evento)"
              value={formatWithdrawalCurrency(item.availableBalance)}
              hint="Valor que a paróquia ainda pode sacar deste evento"
            />
            <PotentialWithdrawalDetailRow
              label="Líquido total das inscrições"
              value={formatWithdrawalCurrency(item.totalParishFee)}
            />
            <PotentialWithdrawalDetailRow
              label="Saques já concluídos"
              value={formatWithdrawalCurrency(item.alreadyWithdrawn)}
            />
            <PotentialWithdrawalDetailRow
              label="Inscrições pagas"
              value={String(item.totalRegistrations)}
            />
          </Box>

          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', pt: 2, mb: 0.5 }}>
            {view === 'pagarme' ? 'Canais — Pagarme' : 'Canais — Mercado Pago'}
          </Typography>
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, px: 1.5, border: '1px solid', borderColor: 'divider' }}>
            {view === 'pagarme' ? (
              <>
                <PotentialWithdrawalDetailRow
                  label="Conta recebedora (API Pagar.me)"
                  value={pagarmeRecipientLabel}
                  hint="Saldo disponível na conta recebedora da paróquia"
                />
                <PotentialWithdrawalDetailRow
                  label="Líquido contábil Pagarme (evento)"
                  value={formatWithdrawalCurrency(item.pagarmeParishNet ?? 0)}
                  hint="Inscrições pagas via Pagar.me neste evento"
                />
                {settlementView === 'mixed' ? (
                  <>
                    <PotentialWithdrawalDetailRow
                      label="MP Pix (evento)"
                      value={formatWithdrawalCurrency(item.mercadopagoPixParishNet ?? 0)}
                    />
                    {(item.mercadopagoAmbiguousParishNet ?? 0) > 0 ? (
                      <PotentialWithdrawalDetailRow
                        label="MP método indefinido"
                        value={formatWithdrawalCurrency(item.mercadopagoAmbiguousParishNet ?? 0)}
                        hint="Conferir extrato — cartão ou Pix não identificado"
                      />
                    ) : null}
                    <PotentialWithdrawalDetailRow
                      label="MP cartão (estimativa)"
                      value={formatWithdrawalCurrency(item.mercadopagoCardAvailableReais ?? 0)}
                      hint="Após alocar saques proporcionais"
                    />
                  </>
                ) : null}
              </>
            ) : (
              <>
                <PotentialWithdrawalDetailRow
                  label="MP cartão (estimativa disponível)"
                  value={formatWithdrawalCurrency(item.mercadopagoCardAvailableReais ?? 0)}
                />
                <PotentialWithdrawalDetailRow
                  label="MP Pix (evento)"
                  value={formatWithdrawalCurrency(item.mercadopagoPixParishNet ?? 0)}
                />
                {(item.mercadopagoAmbiguousParishNet ?? 0) > 0 ? (
                  <PotentialWithdrawalDetailRow
                    label="MP método indefinido"
                    value={formatWithdrawalCurrency(item.mercadopagoAmbiguousParishNet ?? 0)}
                    hint="Conferir extrato antes de marcar como pago"
                  />
                ) : null}
              </>
            )}
          </Box>

          {hasReconciliationWarning ? (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon fontSize="inherit" />}
              sx={{ mt: 2, alignItems: 'flex-start' }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Possível inconsistência de reconciliação
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {item.reconciliationMessages!.map((msg, i) => (
                  <Typography key={i} component="li" variant="body2">
                    {msg}
                  </Typography>
                ))}
              </Box>
            </Alert>
          ) : null}

          {item.eventSlug ? (
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1.5 }}>
              Slug: {item.eventSlug} • ID: {item.eventId}
            </Typography>
          ) : null}
        </Box>
      </Collapse>
    </Paper>
  );
}

const Transactions: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [withdrawalTab, setWithdrawalTab] = useState(0);
  const [potentialSettlementTab, setPotentialSettlementTab] = useState<PotentialSettlementTab>('pagarme');
  const [expandedPotentialId, setExpandedPotentialId] = useState<string | null>(null);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [rejectDialog, setRejectDialog] = useState<{
    open: boolean;
    request: AdminWithdrawalRequest | null;
    reason: string;
  }>({ open: false, request: null, reason: '' });
  const [approveDialog, setApproveDialog] = useState<{
    open: boolean;
    request: AdminWithdrawalRequest | null;
    receipt: File | null;
  }>({ open: false, request: null, receipt: null });
  const [markAsPaidDialog, setMarkAsPaidDialog] = useState<{
    open: boolean;
    item: PotentialWithdrawal | null;
    amount: string;
  }>({ open: false, item: null, amount: '' });

  // Tab 0: Saques possíveis (eventos com saldo que ainda não solicitaram) | 1: Em andamento | 2: Atendidas | 3: Rejeitadas
  const statusParam = ['pending,processing', 'completed', 'rejected,cancelled'][withdrawalTab - 1];

  const defaultSuspendedMessage = 'Estamos suspendendo os saques por um momento. Retornaremos em instantes.';
  const defaultDisabledMessage = 'Os saques estão desativados no momento. Em breve normalizaremos.';

  const [withdrawalMode, setWithdrawalMode] = useState<'active' | 'suspended' | 'disabled'>('active');
  const [suspendedMessageDraft, setSuspendedMessageDraft] = useState<string>(defaultSuspendedMessage);
  const [disabledMessageDraft, setDisabledMessageDraft] = useState<string>(defaultDisabledMessage);
  const [withdrawalConfigLoading, setWithdrawalConfigLoading] = useState(false);
  const [withdrawalConfigApplying, setWithdrawalConfigApplying] = useState(false);

  const loadWithdrawalGlobalControl = async () => {
    setWithdrawalConfigLoading(true);
    try {
      const res = await withdrawalRequestService.getWithdrawalGlobalControl();
      const data = res.data;

      setSuspendedMessageDraft(data.suspendedMessage || defaultSuspendedMessage);
      setDisabledMessageDraft(data.disabledMessage || defaultDisabledMessage);

      if (!data.enabled) setWithdrawalMode('disabled');
      else if (data.suspended) setWithdrawalMode('suspended');
      else setWithdrawalMode('active');
    } catch (e: any) {
      toast({
        title: e?.response?.data?.message || 'Erro ao carregar controle global de saques',
        variant: 'destructive',
      });
    } finally {
      setWithdrawalConfigLoading(false);
    }
  };

  useEffect(() => {
    loadWithdrawalGlobalControl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyWithdrawalMode = async () => {
    setWithdrawalConfigApplying(true);
    toast({ title: 'Aplicando controle global de saques...' });
    try {
      console.log('[withdrawals] apply mode=' + withdrawalMode);
      await withdrawalRequestService.setWithdrawalGlobalControl({
        mode: withdrawalMode,
        suspendedMessage: suspendedMessageDraft,
        disabledMessage: disabledMessageDraft,
      });

      toast({ title: 'Configuração global de saque aplicada com sucesso' });
      queryClient.invalidateQueries({ queryKey: ['admin-withdrawal-potential'] });
      queryClient.invalidateQueries({ queryKey: ['admin-withdrawal-requests'] });
      loadWithdrawalGlobalControl();
    } catch (e: any) {
      console.error('[withdrawals] applyWithdrawalMode failed:', e);
      toast({
        title:
          e?.response?.data?.message ||
          `Erro ao aplicar configuração global de saque (HTTP ${e?.response?.status ?? '??'})`,
        variant: 'destructive',
      });
    } finally {
      setWithdrawalConfigApplying(false);
    }
  };

  const potentialQuery = useQuery({
    queryKey: ['admin-withdrawal-potential'],
    queryFn: () => withdrawalRequestService.getPotential(),
    select: (res) => res.data || [],
    enabled: withdrawalTab === 0,
  });

  const withdrawalQuery = useQuery({
    queryKey: ['admin-withdrawal-requests', withdrawalTab, statusParam],
    queryFn: () => withdrawalRequestService.getAll(statusParam),
    select: (res) => res.data || [],
    enabled: withdrawalTab !== 0,
  });

  const approveMutation = useMutation({
    mutationFn: ({ requestId, receipt }: { requestId: string; receipt: File }) =>
      withdrawalRequestService.approve(requestId, receipt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-withdrawal-requests'] });
      toast({ title: 'Saque confirmado. A paróquia recebeu o e-mail com o comprovante.' });
      setActioningId(null);
      setApproveDialog({ open: false, request: null, receipt: null });
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message || 'Erro ao atender solicitação',
        variant: 'destructive',
      });
      setActioningId(null);
    },
  });

  const handleOpenApprove = (r: AdminWithdrawalRequest) => {
    setApproveDialog({ open: true, request: r, receipt: null });
  };

  const rejectMutation = useMutation({
    mutationFn: ({ requestId, reason }: { requestId: string; reason?: string }) =>
      withdrawalRequestService.reject(requestId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-withdrawal-requests'] });
      toast({ title: 'Solicitação rejeitada' });
      setRejectDialog({ open: false, request: null, reason: '' });
      setActioningId(null);
    },
    onError: () => {
      toast({ title: 'Erro ao rejeitar', variant: 'destructive' });
      setActioningId(null);
    },
  });

  const markAsPaidMutation = useMutation({
    mutationFn: ({ eventId, amount }: { eventId: string; amount: number }) =>
      withdrawalRequestService.markAsPaid(eventId, amount),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-withdrawal-potential'] });
      queryClient.invalidateQueries({ queryKey: ['admin-withdrawal-requests'] });
      toast({ title: 'Saque registrado como realizado' });
      setMarkAsPaidDialog({ open: false, item: null, amount: '' });
      setActioningId(null);
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message || 'Erro ao registrar saque',
        variant: 'destructive',
      });
      setActioningId(null);
    },
  });

  const openMarkAsPaidDialog = (item: PotentialWithdrawal) => {
    setMarkAsPaidDialog({
      open: true,
      item,
      amount: item.availableBalance.toFixed(2).replace('.', ','),
    });
  };

  const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace('.', ',')}`;

  const copyPixKey = async (pixKey?: string) => {
    const value = String(pixKey || '').trim();
    if (!value) {
      toast({ title: 'Esta paróquia não tem chave Pix cadastrada.', variant: 'destructive' });
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: 'Chave Pix copiada' });
    } catch {
      toast({ title: 'Não foi possível copiar a chave Pix.', variant: 'destructive' });
    }
  };

  const potentialItems = useMemo(() => potentialQuery.data || [], [potentialQuery.data]);
  const potentialCounts = useMemo(() => {
    const pagarme = potentialItems.filter((p) => matchesPotentialSettlementTab(p, 'pagarme')).length;
    const legacy = potentialItems.filter((p) => matchesPotentialSettlementTab(p, 'legacy')).length;
    return { pagarme, legacy };
  }, [potentialItems]);
  const filteredPotentialItems = useMemo(
    () => potentialItems.filter((p) => matchesPotentialSettlementTab(p, potentialSettlementTab)),
    [potentialItems, potentialSettlementTab]
  );

  const parseAmount = (s: string): number => {
    const n = parseFloat(s.replace(',', '.').replace(/\s/g, ''));
    return Number.isNaN(n) ? 0 : n;
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <AccountBalanceWalletIcon />
              Controle global de saques
            </Box>
          }
          subheader="Ativar, suspender ou desativar saques para todas as paróquias"
          action={
            <Button
              size="small"
              onClick={applyWithdrawalMode}
              disabled={withdrawalConfigApplying || withdrawalConfigLoading}
            >
              {withdrawalConfigApplying ? <CircularProgress size={16} /> : 'Aplicar'}
            </Button>
          }
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {withdrawalConfigLoading ? 'Carregando...' : 'A mudança é aplicada para todos os saques.'}
          </Typography>

          <RadioGroup
            row
            value={withdrawalMode}
            onChange={(e) => setWithdrawalMode(e.target.value as any)}
          >
            <FormControlLabel value="active" control={<Radio />} label="Ativo" />
            <FormControlLabel value="suspended" control={<Radio />} label="Suspenso" />
            <FormControlLabel value="disabled" control={<Radio />} label="Desativado" />
          </RadioGroup>

          {withdrawalMode === 'suspended' && (
            <TextField
              fullWidth
              label="Mensagem (paróquias)"
              value={suspendedMessageDraft}
              onChange={(e) => setSuspendedMessageDraft(e.target.value)}
              multiline
              rows={3}
              sx={{ mt: 2 }}
            />
          )}

          {withdrawalMode === 'disabled' && (
            <TextField
              fullWidth
              label="Mensagem (paróquias)"
              value={disabledMessageDraft}
              onChange={(e) => setDisabledMessageDraft(e.target.value)}
              multiline
              rows={3}
              sx={{ mt: 2 }}
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <AccountBalanceWalletIcon />
              Solicitações de Saque
            </Box>
          }
          subheader="A paróquia solicita o saque por evento; o admin aprova ou rejeita aqui."
        />
        <CardContent>
          <Tabs
            value={withdrawalTab}
            onChange={(_, v) => setWithdrawalTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="Saques possíveis" />
            <Tab label="Em andamento" />
            <Tab label="Atendidas" />
            <Tab label="Rejeitadas" />
          </Tabs>

          {withdrawalTab === 0 ? (
            potentialQuery.isLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : !potentialItems.length ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                Nenhum evento com saldo disponível para saque no momento (ou todos já solicitaram).
              </Typography>
            ) : (
              <>
                <Tabs
                  value={potentialSettlementTab}
                  onChange={(_, v: PotentialSettlementTab) => {
                    setPotentialSettlementTab(v);
                    setExpandedPotentialId(null);
                  }}
                  sx={{ mb: 2 }}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab
                    value="pagarme"
                    label={`Com Pagarme (${potentialCounts.pagarme})`}
                  />
                  <Tab
                    value="legacy"
                    label={`Legado — Mercado Pago (${potentialCounts.legacy})`}
                  />
                </Tabs>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {potentialSettlementTab === 'pagarme'
                    ? 'Eventos com split Pagar.me (ou mistos). Priorize conferir saldo na conta recebedora e o líquido contábil Pagarme.'
                    : 'Eventos anteriores ao split: repasse manual via Mercado Pago (cartão, Pix ou método não classificado).'}
                </Typography>
                {!filteredPotentialItems.length ? (
                  <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                    Nenhum evento nesta visão no momento.
                  </Typography>
                ) : (
                  <Box>
                    {filteredPotentialItems.map((p: PotentialWithdrawal) => (
                      <PotentialWithdrawalCard
                        key={p.eventId}
                        item={p}
                        view={potentialSettlementTab}
                        expanded={expandedPotentialId === p.eventId}
                        onToggle={() =>
                          setExpandedPotentialId((prev) => (prev === p.eventId ? null : p.eventId))
                        }
                        onMarkPaid={() => openMarkAsPaidDialog(p)}
                        markDisabled={!!actioningId}
                      />
                    ))}
                  </Box>
                )}
              </>
            )
          ) : withdrawalQuery.isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : withdrawalQuery.isError ? (
            <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>
              Não foi possível carregar as solicitações. Atualize a página.
            </Typography>
          ) : !withdrawalQuery.data?.length ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Nenhuma solicitação nesta categoria.
            </Typography>
          ) : (
            <List disablePadding>
              {withdrawalQuery.data.map((r) => (
                <ListItem key={r.id} divider sx={{ px: 0 }}>
                  <ListItemText
                    secondaryTypographyProps={{ component: 'div' }}
                    primary={
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Typography variant="body1" fontWeight="medium">
                          {r.eventTitle}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {r.parishName} •{' '}
                          {r.parishCity && r.parishState
                            ? `${r.parishCity} / ${r.parishState}`
                            : '—'}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'block', mt: 0.5 }}>
                        <Typography variant="body2" component="span">
                          Valor: {formatCurrency(r.amount)} •{' '}
                          {r.status === 'pending'
                            ? 'Aguardando pagamento'
                            : r.status === 'processing'
                            ? 'Código enviado'
                            : r.status === 'completed'
                            ? 'Atendido'
                            : r.status === 'cancelled'
                            ? 'Cancelado'
                            : 'Rejeitado'}
                          {r.requestedAt &&
                            ` • ${new Date(r.requestedAt).toLocaleDateString('pt-BR')}`}
                          {r.rejectionReason && ` • Motivo: ${r.rejectionReason}`}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                          <Typography variant="body2" component="span" sx={{ wordBreak: 'break-all' }}>
                            {r.pixKey
                              ? `Pix ${pixKeyLabel(r.pixKeyType)}: ${r.pixKey}`
                              : 'Chave Pix não cadastrada'}
                          </Typography>
                          {r.pixKey ? (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ContentCopyIcon />}
                              onClick={() => copyPixKey(r.pixKey)}
                            >
                              Copiar Pix
                            </Button>
                          ) : null}
                        </Stack>
                      </Box>
                    }
                  />
                  {withdrawalTab === 1 &&
                    ['pending', 'processing'].includes(r.status) && (
                      <ListItemSecondaryAction>
                        <Button
                          size="small"
                          color="success"
                          variant="contained"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleOpenApprove(r)}
                          disabled={!!actioningId || approveMutation.isPending}
                        >
                          Aprovar
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={() =>
                            setRejectDialog({
                              open: true,
                              request: r,
                              reason: '',
                            })
                          }
                          disabled={!!actioningId}
                          sx={{ ml: 1 }}
                        >
                          Rejeitar
                        </Button>
                      </ListItemSecondaryAction>
                    )}
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={approveDialog.open}
        onClose={() => {
          if (!approveMutation.isPending) {
            setApproveDialog({ open: false, request: null, receipt: null });
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar pagamento</DialogTitle>
        <DialogContent>
          {approveDialog.request && (
            <>
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  DADOS PARA O PIX MANUAL
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75 }}>
                  {approveDialog.request.parishName} • {formatCurrency(approveDialog.request.amount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {approveDialog.request.eventTitle}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                    {approveDialog.request.pixKey
                      ? `${pixKeyLabel(approveDialog.request.pixKeyType)}: ${approveDialog.request.pixKey}`
                      : 'Chave Pix não cadastrada'}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<ContentCopyIcon />}
                    disabled={!approveDialog.request.pixKey}
                    onClick={() => copyPixKey(approveDialog.request?.pixKey)}
                  >
                    Copiar Pix
                  </Button>
                </Stack>
              </Paper>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Anexe o comprovante do Pix. Ao confirmar, a paróquia recebe um e-mail com esse arquivo.
              </Typography>
              <Button variant="outlined" component="label" fullWidth disabled={approveMutation.isPending}>
                {approveDialog.receipt ? approveDialog.receipt.name : 'Anexar comprovante'}
                <input
                  hidden
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setApproveDialog((p) => ({ ...p, receipt: file }));
                  }}
                />
              </Button>
              {!approveDialog.receipt ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  PDF, JPG, PNG ou WEBP. O anexo é obrigatório.
                </Typography>
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApproveDialog({ open: false, request: null, receipt: null })}
            disabled={approveMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              if (approveDialog.request && approveDialog.receipt) {
                setActioningId(approveDialog.request.id);
                approveMutation.mutate({
                  requestId: approveDialog.request.id,
                  receipt: approveDialog.receipt,
                });
              }
            }}
            disabled={!approveDialog.receipt || approveMutation.isPending}
          >
            {approveMutation.isPending ? <CircularProgress size={24} color="inherit" /> : 'Confirmar pagamento'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectDialog.open}
        onClose={() =>
          setRejectDialog({ open: false, request: null, reason: '' })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rejeitar solicitação</DialogTitle>
        <DialogContent>
          {rejectDialog.request && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {rejectDialog.request.eventTitle} •{' '}
              {formatCurrency(rejectDialog.request.amount)}
            </Typography>
          )}
          <TextField
            fullWidth
            label="Motivo"
            value={rejectDialog.reason}
            onChange={(e) =>
              setRejectDialog((p) => ({ ...p, reason: e.target.value }))
            }
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setRejectDialog({ open: false, request: null, reason: '' })
            }
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (rejectDialog.request) {
                setActioningId(rejectDialog.request.id);
                rejectMutation.mutate({
                  requestId: rejectDialog.request.id,
                  reason: rejectDialog.reason,
                });
              }
            }}
            disabled={rejectMutation.isPending || !rejectDialog.reason.trim()}
          >
            Rejeitar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={markAsPaidDialog.open}
        onClose={() => {
          if (!markAsPaidMutation.isPending) {
            setMarkAsPaidDialog({ open: false, item: null, amount: '' });
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Registrar saque realizado</DialogTitle>
        <DialogContent>
          {markAsPaidDialog.item && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Evento: <strong>{markAsPaidDialog.item.eventTitle}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Paróquia: {markAsPaidDialog.item.parishName}
                {(markAsPaidDialog.item.parishCity || markAsPaidDialog.item.parishState) &&
                  ` • ${[markAsPaidDialog.item.parishCity, markAsPaidDialog.item.parishState].filter(Boolean).join(' / ')}`}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Valor total disponível: {formatCurrency(markAsPaidDialog.item.availableBalance)}
              </Typography>
              <TextField
                fullWidth
                label="Valor a registrar (R$)"
                value={markAsPaidDialog.amount}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^\d,]/g, '');
                  const parts = raw.split(',');
                  const formatted =
                    parts.length > 1 ? `${parts[0]},${parts[1].slice(0, 2)}` : raw;
                  setMarkAsPaidDialog((p) => ({ ...p, amount: formatted }));
                }}
                placeholder="0,00"
                helperText="Informe o valor que já foi pago. Pode ser menor que o total se só parte foi repassada."
                inputProps={{ inputMode: 'decimal' }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMarkAsPaidDialog({ open: false, item: null, amount: '' })}
            disabled={markAsPaidMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (!markAsPaidDialog.item) return;
              const amount = parseAmount(markAsPaidDialog.amount);
              if (amount <= 0) {
                toast({ title: 'Informe um valor maior que zero', variant: 'destructive' });
                return;
              }
              if (amount > markAsPaidDialog.item.availableBalance) {
                toast({
                  title: `O valor não pode ser maior que ${formatCurrency(markAsPaidDialog.item.availableBalance)}`,
                  variant: 'destructive',
                });
                return;
              }
              setActioningId(markAsPaidDialog.item.eventId);
              markAsPaidMutation.mutate({
                eventId: markAsPaidDialog.item.eventId,
                amount,
              });
            }}
            disabled={
              markAsPaidMutation.isPending ||
              !markAsPaidDialog.amount ||
              parseAmount(markAsPaidDialog.amount) <= 0
            }
          >
            {markAsPaidMutation.isPending ? <CircularProgress size={24} color="inherit" /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Transactions;
